/**
 * Preset mock data + a shim HessianClient that returns it synchronously.
 *
 * Lets us mount the real private-dashboard component on the public splash
 * page without any network calls. The shim's `createQuery` ignores accessor
 * args and returns a plain object shaped like a TanStack CreateQueryResult —
 * the dashboard only reads `.data` and `.isLoading`, so plain fields are
 * enough (no subscribe / refetch semantics needed for a static screenshot-y
 * preview).
 */

// Anchor mock timestamps to "now" at module load. The dashboard's 24h KPI
// window (throughput / failure-rate sparklines) filters by real `Date.now()`,
// so a fixed historical anchor would put every mock run outside the window
// and leave the cards empty. Computing `MOCK_NOW` at import time keeps the
// numbers always-recent. SSR vs. hydration drift is well under a minute, and
// `relativeTime` rounds to whole minutes — no visible churn.
const MOCK_NOW = Date.now();
const mockDate = (minutesAgo: number) => new Date(MOCK_NOW - minutesAgo * 60 * 1000).toISOString();

const workflows = [
	{ id: 'wf_triage', name: 'triage_ticket', status: 'active' },
	{ id: 'wf_summary', name: 'summarize_pr', status: 'active' },
	{ id: 'wf_onboard', name: 'onboard_user', status: 'active' },
	{ id: 'wf_report', name: 'daily_report', status: 'active' },
	{ id: 'wf_invoice', name: 'process_invoice', status: 'active' },
	{ id: 'wf_sync', name: 'crm_sync', status: 'active' },
	{ id: 'wf_notify', name: 'notify_oncall', status: 'active' },
	{ id: 'wf_embed', name: 'embed_documents', status: 'active' },
	{ id: 'wf_legacy', name: 'legacy_batch_etl', status: 'inactive' },
	{ id: 'wf_deprec', name: 'deprecated_hook', status: 'inactive' },
	{ id: 'wf_draft1', name: 'experimental_agent', status: 'draft' },
	{ id: 'wf_draft2', name: 'beta_workflow', status: 'draft' }
];

// One `failed` in the cycle gives the failure-rate KPI a realistic non-zero
// reading (~7% across the 15 mock runs) instead of a flat 0%. Positioning
// it deep in the cycle (index 9 of 12) places the failure in the older half
// of the 24h window once spread across time below — so the failure-rate
// delta trends down (improving / green) instead of up (regressing / red).
const runStatuses = [
	'completed',
	'completed',
	'completed',
	'completed',
	'running',
	'completed',
	'completed',
	'completed',
	'completed',
	'failed',
	'running',
	'completed'
];
const runNames = [
	'vibrant_mirzakhani',
	'romantic_cohen',
	'brave_gates',
	'confident_beaver',
	'focused_euclid',
	'sharp_pike',
	'eloquent_lamport',
	'dazzling_pasteur',
	'stoic_curie',
	'practical_tesla',
	'modest_babbage',
	'nostalgic_hopper',
	'priceless_kepler',
	'kind_ramanujan',
	'peaceful_faraday'
];

const runs = runNames.map((name, i) => ({
	id: `run_${name}`,
	name,
	workflow: { name: workflows[i % 8].name },
	status: runStatuses[i % runStatuses.length],
	// Spread runs across the full 24h KPI window (5 min .. ~21h ago) so the
	// throughput sparkline reads as steady activity instead of a single
	// spike in the most-recent bucket. runs[0] stays the most recent so
	// the "Latest run" rail card still shows a fresh entry.
	created_at: mockDate(i * 90 + 5),
	// scatter chart plots duration_ms — spread values to make a nice cloud
	duration_ms: 400 + Math.abs(Math.sin(i * 1.3)) * 14000
}));

const workers = [
	{ name: 'worker-prod-a7f', type: 'docker', status: 'active' },
	{ name: 'worker-prod-b3c', type: 'kubernetes', status: 'active' },
	{ name: 'worker-prod-c9e', type: 'kubernetes', status: 'active' },
	{ name: 'worker-etl-01', type: 'docker', status: 'active' },
	{ name: 'worker-batch-02', type: 'local', status: 'active' },
	{ name: 'worker-canary', type: 'docker', status: 'active' },
	{ name: 'worker-eu-west', type: 'kubernetes', status: 'active' },
	{ name: 'worker-retired-1', type: 'docker', status: 'inactive' }
];

const queueGroups = [
	{ id: 'qg_default', name: 'default', description: 'Catch-all queue for untagged tasks', default: true },
	{ id: 'qg_gpu', name: 'gpu', description: 'GPU-backed workers for model inference', default: false },
	{ id: 'qg_batch', name: 'batch', description: 'Nightly ETL + reporting jobs', default: false }
];

const auditLogs = [
	{ id: 'a1', action: 'trigger', entity_type: 'run', entity_id: 'run_' + runNames[0], created_at: mockDate(2) },
	{ id: 'a2', action: 'create', entity_type: 'workflow', entity_id: 'wf_draft2', created_at: mockDate(9) },
	{ id: 'a3', action: 'update', entity_type: 'policy', entity_id: 'pol_triage_v2', created_at: mockDate(24) },
	{ id: 'a4', action: 'trigger', entity_type: 'run', entity_id: 'run_' + runNames[2], created_at: mockDate(38) },
	{ id: 'a5', action: 'create', entity_type: 'api_key', entity_id: 'key_worker_us', created_at: mockDate(72) },
	{ id: 'a6', action: 'delete', entity_type: 'worker', entity_id: 'worker-retired-0', created_at: mockDate(120) }
];

export const MOCK_ORGANIZATION_ID = 'org_splash_preview';

const organizations = [
	{
		id: MOCK_ORGANIZATION_ID,
		name: 'Acme Inc',
		slug: 'acme',
		personal: false
	}
];

// Fake entity DAG for the dashboard's "Latest run" rail card. The card
// queries `/v1/runs/{runId}` for the most recent run (runs[0] above) and
// renders the response's `entities` + `edges` as a Tree. Without a mock
// here the card collapses to "No tasks in this run." for the splash.
//
// Shape: a classifier agent fans out to two lookups that both converge
// on a notify task — small enough to fit comfortably in the rail card
// without scrolling, but with one diamond fan-in so the dashboard's
// duplicate-on-multiple-parents render emits a `· shared` row (the
// "real DAG, not a flat list" tell). All entities are `completed` to
// match runs[0]'s status.
const latestRunEntities = [
	{ id: 'ent_classify', queue_group_id: 'qg_default', entity_id: 'classify_intent', entity_type: 'agent', name: 'classify_intent', status: 'completed', enqueued_at: mockDate(6), retry_count: 0, agent_provider: 'anthropic' },
	{ id: 'ent_lookup',   queue_group_id: 'qg_default', entity_id: 'lookup_customer', entity_type: 'task',  name: 'lookup_customer', status: 'completed', enqueued_at: mockDate(5), retry_count: 0 },
	{ id: 'ent_priority', queue_group_id: 'qg_default', entity_id: 'assess_priority', entity_type: 'task',  name: 'assess_priority', status: 'completed', enqueued_at: mockDate(5), retry_count: 0 },
	{ id: 'ent_notify',   queue_group_id: 'qg_default', entity_id: 'notify_team',     entity_type: 'task',  name: 'notify_team',     status: 'completed', enqueued_at: mockDate(3), retry_count: 0 }
];

const latestRunEdges = [
	{ sourceId: 'ent_classify', targetId: 'ent_lookup'   },
	{ sourceId: 'ent_classify', targetId: 'ent_priority' },
	{ sourceId: 'ent_lookup',   targetId: 'ent_notify'   },
	{ sourceId: 'ent_priority', targetId: 'ent_notify'   }
];

const MOCK_RESPONSES: Record<string, unknown> = {
	'get /v1/workflows': { workflows },
	'get /v1/runs': { runs },
	// Same payload for any specific-run lookup — the mock client keys
	// on the literal `{runId}` template path, not the resolved URL, so
	// every `/v1/runs/{runId}` call gets the same fake DAG. That's fine
	// for the splash: only the latest run feeds the rail card.
	'get /v1/runs/{runId}': { entities: latestRunEntities, edges: latestRunEdges },
	'get /v1/workers': { workers },
	'get /v1/queue_groups': { queue_groups: queueGroups },
	'get /v1/audit': { logs: auditLogs },
	'get /v1/organizations': organizations
};

export const mockOrganization = organizations[0];

/**
 * Splash-only overrides for the two KPI tiles whose backing metrics endpoint
 * doesn't exist yet (p95 duration, queue wait). Real dashboard keeps the
 * "awaiting metrics endpoint" stub; the splash gets plausible numbers and a
 * shaped sparkline so the tile reads as a healthy production system.
 *
 * Values are deliberately gentle (sub-3s p95, sub-1s queue wait) and trend
 * mildly downward, matching the "things are working" vibe the showcase wants.
 */
export type SplashMetricCard = {
	value: string;
	sub: string;
	delta: string;
	deltaDirection: 'up' | 'down';
	values: number[];
};
export type SplashMetricCards = {
	p95Duration: SplashMetricCard;
	queueWait: SplashMetricCard;
};
export const SPLASH_METRIC_CARDS: SplashMetricCards = {
	p95Duration: {
		// Kept in step with the Workflow Runs scatter plot below the tiles —
		// the mock `runs` array's `duration_ms` distribution has a true p95
		// around 14s, so a sub-3s reading here reads as inconsistent next to
		// dots clearly plotted near the 14s line.
		value: '14.2s',
		sub: 'bucketed p95 latency over 24h',
		delta: '−0.8s vs prior 12h',
		deltaDirection: 'up',
		values: [15.4, 15.1, 14.9, 15.0, 14.7, 14.6, 14.5, 14.3, 14.4, 14.1, 14.0, 14.2]
	},
	queueWait: {
		// Render as fractional seconds so the value glyph stays ~4 chars wide
		// and matches the rhythm of the neighbouring `2.4s` p95 tile. At the
		// MetricCard's 42px display size, longer strings like `680ms` push
		// the delta off the baseline row and wrap "12h" to a second line.
		value: '0.7s',
		sub: 'time from enqueue to worker pickup',
		delta: '−0.04s vs prior 12h',
		deltaDirection: 'up',
		values: [0.82, 0.79, 0.76, 0.74, 0.72, 0.7, 0.69, 0.7, 0.68, 0.67, 0.66, 0.68]
	}
};

/**
 * Stub Supabase user wired into the splash showcase's `PrivateAppShell`
 * so the UserNav row renders with a name + email + avatar initials
 * instead of the empty "User" fallback. Shape matches the subset of
 * `@supabase/supabase-js`'s `User` that the shell actually reads
 * (`email`, `user_metadata.name`, `user_metadata.picture` /
 * `avatar_url`). Cast loose at the call site rather than reaching
 * for the full `User` shape — the showcase only needs the display
 * fields and the rest would be dead ballast in the mock.
 */
export const mockUser = {
	email: 'john.smith@acme.com',
	user_metadata: { name: 'John Smith' }
};

/**
 * Build a minimal HessianClient that the dashboard can bind against without
 * hitting the network. Typed loosely so we don't have to reimplement the
 * entire OpenapiQueryClient surface — the dashboard only touches createQuery.
 */
export function createMockHessianClient() {
	const client = {
		createQuery: (method: string, path: string) => {
			const data = MOCK_RESPONSES[`${method} ${path}`];
			return {
				data,
				isLoading: false,
				isError: false,
				isSuccess: true,
				error: null,
				status: 'success',
				fetchStatus: 'idle',
				refetch: () => Promise.resolve({ data }),
				isFetching: false,
				isPending: false,
				isStale: false
			};
		},
		createInfiniteQuery: () => ({
			data: { pages: [], pageParams: [] },
			isLoading: false,
			hasNextPage: false,
			fetchNextPage: () => Promise.resolve(),
			isFetchingNextPage: false
		}),
		createMutation: () => ({
			mutate: () => undefined,
			mutateAsync: () => Promise.resolve(),
			isPending: false,
			isError: false,
			isSuccess: false,
			data: undefined,
			error: null,
			reset: () => undefined
		}),
		invalidateQuery: () => Promise.resolve(),
		queryOptions: () => ({ queryKey: [], queryFn: async () => null }),
		prefetchQuery: () => Promise.resolve(),
		prefetchInfiniteQuery: () => Promise.resolve()
	};
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	return client as any;
}
